/**
 * "Test as a student": the signed-in staff member's own environment in this
 * lab, built exactly like a student's (image, starter code, settings) but on
 * no roster and in no count, grade or analytics. Opening it needs no second
 * account and no private window; "Test the D2L link" lets the lab's own D2L
 * link (quiz preview, LockDown Browser) reach it for a while.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FlaskConical, ExternalLink, RotateCcw, Trash2, Link2, FileDown } from 'lucide-react';
import { labs } from '../services/api';

const POLL_MS = 3000;
const OPEN_TIMEOUT_MS = 5 * 60 * 1000;

const STATE_LABELS = {
  running: 'Running', creating: 'Starting', stopping: 'Stopping', stopped: 'Paused',
  error: 'Needs attention', unknown: 'Unknown'
};
const STATE_COLORS = {
  running: 'var(--accent-success)', creating: 'var(--accent-primary)', stopping: 'var(--accent-warning)',
  stopped: 'var(--text-muted)', error: 'var(--accent-danger)', unknown: 'var(--text-muted)'
};

const errorMessage = (error, fallback) => error?.response?.data?.error || error?.message || fallback;
const timeOf = (value) => new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
// The time alone today, the day too before that.
const whenOf = (value) => (new Date(value).toDateString() === new Date().toDateString()
  ? timeOf(value)
  : new Date(value).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }));
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function TestEnvironmentCard({ labId }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(null); // 'open' | 'starter' | 'start-over' | 'discard' | 'd2l'
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const cancelled = useRef(false);
  useEffect(() => () => { cancelled.current = true; }, []);

  const query = useQuery({
    queryKey: ['testEnvironment', labId],
    queryFn: () => labs.testEnvironment(labId),
    // Follow a start or a pause without a manual refresh.
    refetchInterval: (current) => (['creating', 'stopping'].includes(current?.state?.data?.environment?.state) ? 5000 : 30000),
  });
  const info = query.data;
  const environment = info?.environment || null;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['testEnvironment', labId] });

  // The tab opens on the click itself, so no pop-up blocker stands in the way,
  // then follows the IDE's address once the environment is ready.
  const open = async () => {
    setError(null);
    setBusy('open');
    setMessage(environment ? 'Opening your test environment…' : 'Creating your test environment. This takes about a minute.');
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    const deadline = Date.now() + OPEN_TIMEOUT_MS;
    try {
      while (!cancelled.current) {
        const result = await labs.openTestEnvironment(labId);
        if (result.url) {
          if (tab) tab.location.replace(result.url);
          else window.location.assign(result.url);
          setMessage(null);
          return;
        }
        if (Date.now() >= deadline) throw new Error('Your test environment is taking longer than usual to start. Try again in a minute.');
        setMessage('Starting your test environment. This takes about a minute.');
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      }
    } catch (err) {
      if (tab) tab.close();
      setMessage(null);
      setError(errorMessage(err, 'Could not open your test environment.'));
    } finally {
      setBusy(null);
      refresh();
    }
  };

  const run = async (kind, action, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setError(null);
    setMessage(null);
    setBusy(kind);
    try {
      await action();
    } catch (err) {
      setError(errorMessage(err, 'That did not work. Try again.'));
    } finally {
      setBusy(null);
      refresh();
    }
  };

  const linkOpenUntil = info?.d2lLink?.openUntil && new Date(info.d2lLink.openUntil) > new Date()
    ? info.d2lLink.openUntil : null;
  const state = environment?.operation === 'remove' ? 'stopping' : environment?.state;
  const disabled = !!busy || !info?.canTest;
  const starterFiles = info?.starterCode?.files || 0;
  const starterLoadedAt = environment?.starterLoadedAt || null;
  const starterChangedAt = info?.starterCode?.changedAt || null;
  const starterOutdated = !!(starterLoadedAt && starterChangedAt && new Date(starterChangedAt) > new Date(starterLoadedAt));
  // Only once its first copy is known: an environment built before 1.64 never said when.
  const starterNote = !environment || !starterFiles || !starterLoadedAt ? null : starterOutdated
    ? `The lab's starter code changed at ${whenOf(starterChangedAt)}, after your test environment got it (${whenOf(starterLoadedAt)}). `
      + `${state === 'running' ? 'Reload it' : 'Open your test environment, then reload it,'} to try the new version.`
    : `Starter code loaded ${whenOf(starterLoadedAt)}: the lab's current version.`;

  const loadStarterCode = () => run('starter', async () => {
    const result = await labs.loadTestStarterCode(labId);
    setMessage(`Starter code loaded: ${plural(result.files, 'file')}. An open IDE shows the new files right away.`);
  }, 'Copy the lab\'s current starter code into your test workspace? A file you have at the same path is replaced; your other files stay. To begin again from an empty workspace, use Start over.');

  return (
    <div className="card" style={{ marginBottom: 24 }} aria-labelledby="test-environment-title">
      {/* Wraps as a unit on a narrow screen: title and state, then the actions. */}
      <div className="card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
        <h2 id="test-environment-title" style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <FlaskConical size={20} aria-hidden="true" style={{ flexShrink: 0 }} />
          <span style={{ whiteSpace: 'nowrap' }}>Test as a student</span>
          {environment && (
            <span style={{
              padding: '4px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500,
              background: `color-mix(in srgb, ${STATE_COLORS[state] || STATE_COLORS.unknown} 14%, transparent)`,
              color: STATE_COLORS[state] || STATE_COLORS.unknown,
            }}>
              {STATE_LABELS[state] || STATE_LABELS.unknown}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={open} disabled={disabled}>
            {busy === 'open' ? <div className="spinner" style={{ width: 14, height: 14 }} aria-hidden="true" /> : <ExternalLink size={14} aria-hidden="true" />}
            {environment ? 'Open' : 'Open test environment'}
          </button>
          {environment && (
            <>
              {starterFiles > 0 && (
                <button className="btn btn-secondary btn-sm" disabled={disabled || state !== 'running'} onClick={loadStarterCode}
                  title={state === 'running' ? undefined : 'Open your test environment first: starter code goes into a running one.'}>
                  {busy === 'starter' ? <div className="spinner" style={{ width: 14, height: 14 }} aria-hidden="true" /> : <FileDown size={14} aria-hidden="true" />}
                  {starterLoadedAt ? 'Reload starter code' : 'Load starter code'}
                </button>
              )}
              <button className="btn btn-secondary btn-sm" disabled={disabled}
                onClick={() => run('start-over', () => labs.startTestEnvironmentOver(labId),
                  'Start over? Your test workspace is deleted and rebuilt with the lab\'s current starter code, image and settings.')}>
                {busy === 'start-over' ? <div className="spinner" style={{ width: 14, height: 14 }} aria-hidden="true" /> : <RotateCcw size={14} aria-hidden="true" />}
                Start over
              </button>
              <button className="btn btn-secondary btn-sm" disabled={!!busy}
                aria-label="Discard your test environment" title="Discard your test environment"
                onClick={() => run('discard', () => labs.discardTestEnvironment(labId),
                  'Discard your test environment? Its workspace is deleted. Students are not affected.')}>
                {busy === 'discard' ? <div className="spinner" style={{ width: 14, height: 14 }} aria-hidden="true" /> : <Trash2 size={14} aria-hidden="true" />}
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ padding: '16px 20px', display: 'grid', gap: 14 }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, maxWidth: '72ch' }}>
          Try this lab exactly as your students will: the same image, starter code and settings, before you give it to them.
          It is yours alone: it is not on the roster or in any section, and it is left out of counts, grading and analytics.
          {info && ` It pauses after ${info.idleMinutes} minutes without use and is removed after ${info.retentionDays} days unused.`}
        </p>

        {info && !info.canTest && <p role="status" style={{ margin: 0, fontSize: 14 }}>{info.reason}</p>}
        {starterNote && (
          <p style={{ margin: 0, fontSize: 13, color: starterOutdated ? 'var(--accent-warning)' : 'var(--text-muted)' }}>{starterNote}</p>
        )}
        {message && <p role="status" aria-live="polite" style={{ margin: 0, fontSize: 14 }}>{message}</p>}
        {error && <p role="alert" style={{ margin: 0, fontSize: 14, color: 'var(--accent-danger)' }}>{error}</p>}
        {query.isError && <p role="alert" style={{ margin: 0, fontSize: 14, color: 'var(--accent-danger)' }}>
          {errorMessage(query.error, 'Could not read your test environment.')}
        </p>}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          paddingTop: 14, borderTop: '1px solid var(--border-primary)' }}>
          <button className="btn btn-secondary btn-sm" disabled={disabled}
            onClick={() => run('d2l', () => labs.openTestD2LLink(labId))}>
            {busy === 'd2l' ? <div className="spinner" style={{ width: 14, height: 14 }} aria-hidden="true" /> : <Link2 size={14} aria-hidden="true" />}
            {linkOpenUntil ? 'Keep the D2L link open longer' : 'Test the D2L link'}
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: '64ch' }} role="status" aria-live="polite">
            {linkOpenUntil
              ? `Until ${timeOf(linkOpenUntil)}, opening this lab's link in D2L as yourself (quiz preview or LockDown Browser) lands in your test environment.`
              : `Opens this lab's D2L link to your own UDST ID for ${info?.d2lLink?.minutes || 30} minutes, so you can test it from D2L as a student would.`}
          </span>
        </div>
      </div>
    </div>
  );
}
